import {
  AuthResponse,
  changeExpiredPassword,
  Credentials,
  getMfaMethods,
  getPasswordPolicy,
  login,
  MfaMethod,
  PasswordExpiredResponse,
  PasswordPolicy,
  Right,
  startMfaEmailChallenge,
  startReauthEmailChallenge,
  verifyLoginMfaTotp,
  verifyMfaEmailChallenge,
  verifyReauthEmailChallenge,
} from "@/api/openapi";
import { PspContext } from "@/config/contexts";
import { useIsTablet } from "@/config/hooks";
import {
  ensurePushSubscribed,
  ensurePushUnsubscribed,
  watchNotificationPermission,
} from "@/config/push";
import { translate } from "@/config/translator";
import { PasswordPolicyError } from "@/config/types";
import {
  getAccessToken,
  setAccessTokenStorage,
  setActorIdStorage,
  setPasswordExpiredTokenStorage,
  setPreAuthTokenStorage,
  setRefreshTokenStorage,
  setSelectedMandateIdStorage,
  validatePassword,
} from "@/config/utils";
import { KeyboardEvent, useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { getPasswordInfo } from "../app/settings/passwordUtils";

export const PSP_EMAIL_REGEX = /.*@psp-bz\.it/;
const TOTP_CODE_LENGTH = 6;
const EMAIL_CODE_LENGTH = 6;

type LoginLocationState =
  | {
      from?: string;
      reauth?: boolean;
    }
  | undefined;

type LoginStage =
  | "email"
  | "password"
  | "select2faMethod"
  | "inputAuthenticatorCode"
  | "inputEmailCode"
  | "passwordExpired";

type CodeWarning = "wrong" | "tooShort" | "tooLong";

export function useLogin() {
  const {
    lang,
    setLang,
    setUser,
    selectedMandateId,
    setSelectedMandateId,
    setSelectedMandate,
    selectedMandate,
    setCookieConsentDialogOpen,
    setActorId,
  } = useContext(PspContext);

  const navigate = useNavigate();
  const isTablet = useIsTablet();
  const location = useLocation();
  const locationState = location.state as LoginLocationState;

  const passwordInputRef = useRef<HTMLInputElement>(null);
  const totpInputRef = useRef<HTMLInputElement>(null);
  const emailCodeInputRef = useRef<HTMLInputElement>(null);
  const emailChallengeIdRef = useRef<string>(null);
  const resendEmailCodeCooldownIntervalRef = useRef<NodeJS.Timeout>(null);
  const newPasswordConfirmInputRef = useRef<HTMLInputElement>(null);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showInvalidCredentialsMessage, setShowInvalidCredentialsMessage] =
    useState(false);
  const [loginStage, setLoginStage] = useState<LoginStage>("email");
  const [isLoadingLogin, setIsLoadingLogin] = useState(false);
  const [mfaMethods, setMfaMethods] = useState<MfaMethod[]>();
  const [selectedMfaMethod, setSelectedMfaMethod] = useState<MfaMethod | null>(
    null,
  );
  const [totpCode, setTotpCode] = useState("");
  const [emailCode, setEmailCode] = useState("");
  const [resendEmailCodeCooldownSeconds, setResendEmailCodeCooldownSeconds] =
    useState<number | null>(null);
  const [emailCodeWarning, setEmailCodeWarning] = useState<CodeWarning | null>(
    null,
  );
  const [totpCodeWarning, setTotpCodeWarning] = useState<CodeWarning | null>(
    null,
  );
  const [trustDevice, setTrustDevice] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [newPasswordConfirm, setNewPasswordConfirm] = useState("");
  const [passwordRequirements, setPasswordRequirements] = useState<
    Record<PasswordPolicyError, boolean>
  >({
    missing_digit: true,
    missing_uppercase_letter: true,
    missing_lowercase_letter: true,
    too_short: true,
    missing_symbol: true,
  });
  const [passwordPolicy, setPasswordPolicy] = useState<PasswordPolicy>();

  useEffect(() => {
    if (getAccessToken() && !locationState?.from) {
      navigate("/home");
      return;
    }
    if (locationState?.reauth) {
      fetchMfaMethods();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationState?.from, locationState?.reauth, navigate]);

  // -----------------------------------------------------------------------------
  // EVENT HANDLERS
  // -----------------------------------------------------------------------------
  function onContinue() {
    if (loginStage === "email") {
      if (PSP_EMAIL_REGEX.test(email.trim())) {
        onPspLogin();
      } else {
        setLoginStage("password");
      }
    } else if (loginStage === "select2faMethod") {
      if (selectedMfaMethod && selectedMfaMethod.mfaType === "email") {
        sendEmailCode(selectedMfaMethod.id);
      } else if (selectedMfaMethod && selectedMfaMethod.mfaType === "totp") {
        setLoginStage("inputAuthenticatorCode");
      }
    } else if (
      loginStage === "inputAuthenticatorCode" ||
      loginStage === "inputEmailCode"
    ) {
      onMfaLogin();
    } else if (loginStage === "passwordExpired") {
      onResetPassword();
    } else {
      onLogin();
    }
  }

  const { passwordInvalid, passwordIsEmpty, passwordsDontMatch } =
    getPasswordInfo(passwordRequirements, newPassword, newPasswordConfirm);

  const canChangePassword =
    !passwordInvalid && !passwordIsEmpty && !passwordsDontMatch;

  function onResetPassword() {
    if (!canChangePassword) return;
    changeExpiredPassword({ body: { newPassword } }).then(
      ({ data, error, status }) => {
        if (error) {
          if (status === 400) {
            toast(translate("cannotReuseOneOfLast5Passwords", lang));
            return;
          }
          if (status === 401) {
            toast(translate("sessionTimedOut", lang));
            setAccessTokenStorage(null);
            setPasswordExpiredTokenStorage(null);
            setPreAuthTokenStorage(null);
            return;
          }
          toast(translate("somethingWentWrong", lang));
          return;
        }

        onAuthResponse(data);
      },
    );
  }

  function fetchMfaMethods() {
    getMfaMethods({
      query: { active_only: true, verified_only: true },
    }).then(({ data, error }) => {
      setIsLoadingLogin(false);
      if (error) {
        toast(translate("somethingWentWrong", lang));
      } else {
        setMfaMethods(
          data.sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary)),
        );
        setLoginStage("select2faMethod");
      }
    });
  }

  function onPasswordExpiredResponse(data: PasswordExpiredResponse) {
    setPasswordExpiredTokenStorage(data.passwordExpiredToken);
    setLoginStage("passwordExpired");
    getPasswordPolicy().then(({ data, error }) => {
      setIsLoadingLogin(false);
      if (error) {
        toast(translate("somethingWentWrong", lang));
        return;
      }

      setPasswordPolicy(data);
    });
  }

  function onMfaLogin() {
    if (selectedMfaMethod && selectedMfaMethod.mfaType === "totp") {
      const cleanTotp = totpCode.replaceAll(" ", "");
      verifyLoginMfaTotp({
        body: {
          mfaMethodId: selectedMfaMethod.id,
          code: cleanTotp,
          trustDevice,
        },
      }).then(({ error, data, status }) => {
        if (cleanTotp.length < TOTP_CODE_LENGTH) {
          setTotpCodeWarning("tooShort");
          return;
        } else if (cleanTotp.length > TOTP_CODE_LENGTH) {
          setTotpCodeWarning("tooLong");
          return;
        }
        if (error) {
          if (status === 403) {
            setTotpCodeWarning("wrong");
          } else if (status === 401) {
            toast(translate("sessionTimedOut", lang));
          } else {
            toast(translate("somethingWentWrong", lang));
          }
        } else {
          if (data.authType === "auth") onAuthResponse(data);
          else if (data.authType === "password_expired")
            onPasswordExpiredResponse(data);
        }
      });
    } else if (emailChallengeIdRef.current) {
      const cleanEmailCode = emailCode.replaceAll(" ", "");
      if (locationState?.reauth) {
        if (cleanEmailCode.length < EMAIL_CODE_LENGTH) {
          setEmailCodeWarning("tooShort");
          return;
        } else if (cleanEmailCode.length > EMAIL_CODE_LENGTH) {
          setEmailCodeWarning("tooLong");
          return;
        }
        verifyReauthEmailChallenge({
          body: {
            challengeId: emailChallengeIdRef.current,
            code: cleanEmailCode,
            trustDevice,
          },
        }).then(({ error, data, status }) => {
          if (error) {
            if (status === 403) {
              setEmailCodeWarning("wrong");
            } else if (status === 401) {
              toast(translate("sessionTimedOut", lang));
            } else {
              toast(translate("somethingWentWrong", lang));
            }
          } else {
            onAuthResponse(data);
          }
        });
      } else {
        verifyMfaEmailChallenge({
          body: {
            challengeId: emailChallengeIdRef.current,
            code: emailCode,
            trustDevice,
          },
        }).then(({ error, data, status }) => {
          if (error) {
            if (status === 403) {
              setEmailCodeWarning("wrong");
            } else if (status === 401) {
              toast(translate("sessionTimedOut", lang));
            } else {
              toast(translate("somethingWentWrong", lang));
            }
          } else {
            if (data.authType === "auth") onAuthResponse(data);
            else if (data.authType === "password_expired")
              onPasswordExpiredResponse(data);
          }
        });
      }
    }
  }

  function sendEmailCode(methodId: string) {
    setResendEmailCodeCooldownSeconds(60);
    if (locationState?.reauth) {
      startReauthEmailChallenge({ body: { mfaMethodId: methodId } }).then(
        ({ error, data }) => {
          if (error) {
            toast(translate("somethingWentWrong", lang));
          } else {
            setLoginStage("inputEmailCode");
            emailChallengeIdRef.current = data;
          }
        },
      );
    } else {
      startMfaEmailChallenge({ body: { mfaMethodId: methodId } }).then(
        ({ error, data }) => {
          if (error) {
            toast(translate("somethingWentWrong", lang));
          } else {
            setLoginStage("inputEmailCode");
            emailChallengeIdRef.current = data;
          }
        },
      );
    }
  }

  function onBack() {
    if (loginStage === "password") {
      setLoginStage("email");
      setPassword("");
    } else if (loginStage === "select2faMethod") {
      setSelectedMfaMethod(null);
      if (locationState?.reauth && locationState?.from) {
        navigate(locationState.from);
      } else {
        setLoginStage("password");
      }
    } else if (loginStage === "inputAuthenticatorCode") {
      setTotpCode("");
      setLoginStage("select2faMethod");
    } else if (loginStage === "inputEmailCode") {
      setEmailCode("");
      setLoginStage("select2faMethod");
    } else if (loginStage === "passwordExpired") {
      setNewPassword("");
      setNewPasswordConfirm("");
      setPasswordRequirements({
        missing_digit: true,
        missing_uppercase_letter: true,
        missing_lowercase_letter: true,
        too_short: true,
        missing_symbol: true,
      });
      setLoginStage(
        selectedMfaMethod
          ? selectedMfaMethod.mfaType === "totp"
            ? "inputAuthenticatorCode"
            : "inputEmailCode"
          : "select2faMethod",
      );
    }
  }

  function onPspLogin() {
    window.location.assign("/sso-login");
  }

  function onAuthResponse(data: AuthResponse) {
    setAccessTokenStorage(data.accessToken);
    setRefreshTokenStorage(data.refreshToken);
    setActorIdStorage(data.actorId);
    setActorId(data.actorId ?? null);
    setUser(data.user);
    setLang(data.user.language.short);
    watchNotificationPermission(
      () => ensurePushUnsubscribed(data.accessToken),
      () => ensurePushSubscribed(data.accessToken),
    );
    let currentRight: Right | null = null;

    if (!selectedMandate) {
      const currentlySelectedMandateRight = data.user.mandateRights.find(
        (mandateRight) => mandateRight.mandate.id === selectedMandateId,
      );
      const currentlySelectedMandate = currentlySelectedMandateRight?.mandate;
      currentRight = currentlySelectedMandateRight?.right ?? null;

      if (currentlySelectedMandate && currentRight) {
        // context
        setSelectedMandate(currentlySelectedMandate);
        setSelectedMandateId(currentlySelectedMandate.id);

        // local storage

        setSelectedMandateIdStorage(currentlySelectedMandate.id);
      } else {
        // context
        setSelectedMandateId(data.user.mandateRights[0]?.mandate.id);
        setSelectedMandate(data.user.mandateRights[0]?.mandate);

        // local storage
        setSelectedMandateIdStorage(data.user.mandateRights[0]?.mandate.id);
      }
    } else if (!selectedMandateId) {
      setSelectedMandateId(selectedMandate.id);
    }

    setIsLoadingLogin(false);
    navigate(locationState?.from ?? "/home");
  }

  function onLogin() {
    setIsLoadingLogin(true);
    if (email && password) {
      const credentials: Credentials = {
        email,
        password,
      };
      setPasswordExpiredTokenStorage(null);

      login({ body: credentials }).then(({ data, error, status }) => {
        if (error) {
          if (status === 403) {
            setShowInvalidCredentialsMessage(true);
            setIsLoadingLogin(false);
          } else {
            toast(translate("somethingWentWrong", lang));
          }
          return;
        }

        if (data.authType === "auth") {
          onAuthResponse(data);
        } else if (data.authType === "pre_auth") {
          setPreAuthTokenStorage(data.preAuthToken);
          fetchMfaMethods();
        } else if (data.authType === "password_expired") {
          onPasswordExpiredResponse(data);
        }
      });
    }
  }

  function onKeyDownPassword(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Enter") {
      onLogin();
    }
  }

  function onKeyDownEmail(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Enter") {
      if (PSP_EMAIL_REGEX.test(email.trim())) {
        onPspLogin();
      } else {
        onContinue();
      }
    }
  }

  function onKeyDownMfa(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Enter") {
      onMfaLogin();
    }
  }

  useEffect(() => {
    if (selectedMfaMethod && selectedMfaMethod.mfaType === "email") {
      sendEmailCode(selectedMfaMethod.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (resendEmailCodeCooldownSeconds === null) return;

    resendEmailCodeCooldownIntervalRef.current = setInterval(
      () =>
        setResendEmailCodeCooldownSeconds((prev) =>
          prev && prev > 0 ? prev - 1 : null,
        ),
      1000,
    );

    return function () {
      if (resendEmailCodeCooldownIntervalRef.current) {
        clearInterval(resendEmailCodeCooldownIntervalRef.current);
      }
    };
  }, [resendEmailCodeCooldownSeconds]);

  function onChangeEmailCode(value: string) {
    setEmailCode(value);
    setEmailCodeWarning(null);
  }

  function onChangeTotpCode(value: string) {
    setTotpCode(value);
    setTotpCodeWarning(null);
  }

  function onChangeNewPassword(value: string) {
    if (!passwordPolicy) return;
    setNewPassword(value);
    setPasswordRequirements(validatePassword(value, passwordPolicy));
  }

  function onKeyDownNewPassword(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Enter") newPasswordConfirmInputRef.current?.focus();
  }

  function onKeyDownNewPasswordConfirm(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Enter") onContinue();
  }

  return {
    onBack,
    onKeyDownPassword,
    onKeyDownEmail,
    isLoadingLogin,
    loginStage,
    email,
    password,
    lang,
    navigate,
    setEmail,
    setPassword,
    onContinue,
    showInvalidCredentialsMessage,
    passwordInputRef,
    isTablet,
    mfaMethods,
    setSelectedMfaMethod,
    selectedMfaMethod,
    totpCode,
    onChangeTotpCode,
    emailCode,
    onChangeEmailCode,
    sendEmailCode,
    resendEmailCodeCooldownSeconds,
    emailCodeWarning,
    totpCodeWarning,
    emailCodeInputRef,
    totpInputRef,
    trustDevice,
    setTrustDevice,
    onKeyDownMfa,
    newPassword,
    newPasswordConfirm,
    setNewPasswordConfirm,
    passwordRequirements,
    canChangePassword,
    passwordsDontMatch,
    onChangeNewPassword,
    newPasswordConfirmInputRef,
    onKeyDownNewPassword,
    onKeyDownNewPasswordConfirm,
  };
}
